import React from "react";

/**
 * Soft chip tone map (lighter than StatusBadge). Full class strings retained.
 * @type {Record<string, string>}
 */
const CHIP_TONE_CLASSES = {
  neutral: "bg-gray-100 text-gray-700",
  brand: "bg-light-orange text-orange-primary",
  ai: "bg-ai-bg text-ai-text",
  internal: "bg-internal-bg text-internal-text",
  danger: "bg-danger-bg text-danger-dark",
  success: "bg-success-bg text-success",
  info: "bg-info-bg text-info",
};

/**
 * Soft rounded chip for tags such as goals or medical flags.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Chip content.
 * @param {'neutral'|'brand'|'ai'|'internal'|'danger'|'success'|'info'} [props.tone='neutral'] - Color tone (use 'danger' for medical).
 * @param {string} [props.className] - Extra classes.
 */
export const Chip = ({ children, tone = "neutral", className = "" }) => {
  const toneClass = CHIP_TONE_CLASSES[tone] || CHIP_TONE_CLASSES.neutral;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${toneClass} ${className}`}
    >
      {children}
    </span>
  );
};

/**
 * Toggleable filter pill button with an optional count.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.label - Filter label.
 * @param {number} [props.count] - Optional count shown in parentheses.
 * @param {boolean} [props.active] - Whether the filter is active.
 * @param {() => void} [props.onClick] - Click handler.
 */
export const FilterChip = ({ label, count, active = false, onClick }) => {
  const stateClass = active
    ? "bg-light-orange text-orange-primary border border-orange-200"
    : "bg-white text-gray-700 border border-gray-200 hover:bg-orange-50";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${stateClass}`}
    >
      <span>{label}</span>
      {count !== undefined && count !== null && (
        <span className="opacity-70">({count})</span>
      )}
    </button>
  );
};
